import React from 'react';
import { ListFilter } from 'lucide-react';
import { cn } from '../lib/utils';
import { useLocale } from '../contexts/LocaleContext';
import { formatIncludeExcludeCount } from '../utils/filter-option-polarity';
import { MultiSelectFilterContent, type MultiSelectFilterOption } from './MultiSelectFilterDropdown';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

export interface ResourcesFilterMenuEntry {
  key: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  options: MultiSelectFilterOption[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  excludedIds?: string[];
  onExcludedChange?: (ids: string[]) => void;
  allowExclude?: boolean;
  footer?: React.ReactNode;
  createLabel?: (query: string) => string;
  highlight?: boolean;
}

interface ResourcesFilterMenuProps {
  filters: ResourcesFilterMenuEntry[];
  className?: string;
  assignMode?: boolean;
}

export const ResourcesFilterMenu: React.FC<ResourcesFilterMenuProps> = ({ filters, className, assignMode = false }) => {
  const { t } = useLocale();
  const totalSelected = filters.reduce((sum, filter) => {
    const excludedCount = filter.allowExclude ? (filter.excludedIds?.length ?? 0) : 0;
    return sum + filter.selectedIds.length + excludedCount;
  }, 0);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn(
            'flex h-[38px] shrink-0 items-center gap-2 rounded-md border border-input bg-card px-4 text-sm shadow-sm transition-colors hover:bg-accent hover:text-accent-foreground',
            totalSelected > 0 && 'border-foreground/40 bg-accent/50',
            assignMode && 'border-[var(--scribane-green)] bg-[rgba(94,204,128,0.08)]',
            className
          )}
        >
          <ListFilter className="h-4 w-4 text-muted-foreground" />
          <span>{assignMode ? t('assign_values') : t('filters')}</span>
          {totalSelected > 0 && (
            <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-foreground px-1.5 text-xs font-medium text-background">
              {totalSelected}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-[220px] p-2">
        {filters.map((filter) => {
          const excludedCount = filter.allowExclude ? (filter.excludedIds?.length ?? 0) : 0;
          const countLabel = formatIncludeExcludeCount(filter.selectedIds.length, excludedCount);
          return (
            <DropdownMenuSub key={filter.key}>
              <DropdownMenuSubTrigger className={cn(filter.highlight && 'bg-[rgba(94,204,128,0.12)]')}>
                <filter.icon className="h-4 w-4 text-muted-foreground" />
                <span>{filter.label}</span>
                {countLabel && (
                  <span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-foreground px-1.5 text-xs font-medium text-background">
                    {countLabel}
                  </span>
                )}
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="w-[260px] p-2">
                <MultiSelectFilterContent
                  label={filter.label}
                  options={filter.options}
                  selectedIds={filter.selectedIds}
                  onChange={filter.onChange}
                  excludedIds={filter.excludedIds}
                  onExcludedChange={filter.onExcludedChange}
                  allowExclude={filter.allowExclude}
                  footer={filter.footer}
                  createLabel={filter.createLabel}
                />
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
